import { createElement, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command as CommandPrimitive } from "cmdk";
import { ArrowRight, Building2, ClipboardList, CornerDownLeft, Inbox, LogOut, Moon, Plus, Search, Sparkles, Sun, User } from "lucide-react";
import { api } from "@/lib/api";
import { STATUS, INQ_STATUS, CUSTOMER_TYPE } from "@/lib/format";
import { NAV } from "@/components/drift/nav";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------
   ⌘K. Jump to a page, run an action, or search orders, inquiries and
   customers (from two characters). Filtering of the static entries is
   done here so the remote results can be shown as returned.
   ------------------------------------------------------------------ */

const EMPTY = { orders: [], inquiries: [], customers: [] };

/* `icon` is a Lucide component; rendered with createElement so the element stays generic. */
const ico = (C) => (C ? createElement(C, { className: "h-4 w-4", strokeWidth: 1.75, "aria-hidden": true }) : null);

const Entry = ({ onSelect, icon, children, meta, value, testid }) => (
  <CommandPrimitive.Item
    value={value}
    onSelect={onSelect}
    data-testid={testid}
    className="group relative flex cursor-default select-none items-center gap-3 rounded-lg px-2.5 py-2 text-[13.5px] outline-none data-[selected=true]:bg-accent/70"
  >
    {icon && <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary text-muted-foreground group-data-[selected=true]:text-foreground">{ico(icon)}</span>}
    <span className="min-w-0 flex-1 truncate">{children}</span>
    {meta && <span className="shrink-0 text-[12px] text-muted-foreground">{meta}</span>}
    <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-data-[selected=true]:opacity-100" strokeWidth={1.75} aria-hidden />
  </CommandPrimitive.Item>
);

const GROUP = "[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2.5 [&_[cmdk-group-heading]]:text-[11.5px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground";

export function CommandMenu({ open, onOpenChange, onToggleTheme, theme, onLogout }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setQ(""); setResults(EMPTY); } }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(EMPTY); setBusy(false); return undefined; }
    let alive = true;
    setBusy(true);
    const t = setTimeout(() => {
      const enc = encodeURIComponent(term);
      Promise.all([
        api.get(`/drift/orders?q=${enc}`).then((r) => r.data?.items || []).catch(() => []),
        api.get(`/drift/inquiries?q=${enc}`).then((r) => r.data?.items || []).catch(() => []),
        api.get(`/drift/customers?q=${enc}`).then((r) => (Array.isArray(r.data) ? r.data : r.data?.items || [])).catch(() => []),
      ]).then(([orders, inquiries, customers]) => {
        if (!alive) return;
        setResults({ orders: orders.slice(0, 6), inquiries: inquiries.slice(0, 5), customers: customers.slice(0, 5) });
        setBusy(false);
      });
    }, 180);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const go = (to) => { onOpenChange(false); navigate(to); };
  const run = (fn) => { onOpenChange(false); fn(); };

  const term = q.trim().toLowerCase();
  const match = (label) => !term || label.toLowerCase().includes(term);
  const pages = useMemo(() => NAV.filter((n) => match(n.label)), [term]); // eslint-disable-line react-hooks/exhaustive-deps
  const actions = [
    { label: "Nytt oppdrag", icon: Plus, run: () => go("/drift/oppdrag?new=1"), testid: "drift-cmd-new-order" },
    { label: "Ny samtale med assistenten", icon: Sparkles, run: () => go("/drift/ai?new=1"), testid: "drift-cmd-new-ai" },
    { label: theme === "dark" ? "Byt til lyst tema" : "Byt til mørkt tema", icon: theme === "dark" ? Sun : Moon, run: () => run(onToggleTheme), testid: "drift-cmd-theme" },
    { label: "Logg ut", icon: LogOut, run: () => run(onLogout), testid: "drift-cmd-logout" },
  ].filter((a) => match(a.label));

  const ask = q.trim().length >= 3 ? q.trim() : "";
  const hasRemote = results.orders.length + results.inquiries.length + results.customers.length > 0;
  const nothing = !busy && pages.length === 0 && actions.length === 0 && !hasRemote && !ask;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="shadow-pop top-[12vh] max-w-[640px] translate-y-0 gap-0 overflow-hidden rounded-2xl border-0 p-0 data-[state=open]:slide-in-from-top-2" data-testid="drift-command-palette" aria-describedby={undefined}>
        <DialogTitle className="sr-only">Søk eller hopp til</DialogTitle>
        <CommandPrimitive shouldFilter={false} loop className="flex flex-col bg-popover text-popover-foreground">
          <div className="flex items-center gap-3 border-b border-border/70 px-4">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} aria-hidden />
            <CommandPrimitive.Input
              value={q}
              onValueChange={setQ}
              placeholder="Søk i oppdrag, henvendingar og kundar – eller hopp til ei side"
              className="h-[52px] w-full bg-transparent py-3.5 text-[15px] outline-none placeholder:text-muted-foreground/70"
              data-testid="drift-command-palette-input"
              autoFocus
            />
            <span className="kbd">Esc</span>
          </div>
          <CommandPrimitive.List className="max-h-[min(60vh,440px)] overflow-y-auto p-1.5">
            {nothing && <p className="px-3 py-8 text-center text-[13.5px] text-muted-foreground" data-testid="drift-command-palette-empty">Ingen treff på «{q}».</p>}

            {ask && (
              <CommandPrimitive.Group heading="Assistent" className={GROUP}>
                <Entry value="ask-ai" icon={Sparkles} meta="⌘K → AI" onSelect={() => go(`/drift/ai?ask=${encodeURIComponent(ask)}`)} testid="drift-cmd-ask-ai">
                  Spør assistenten: <span className="text-muted-foreground">«{ask}»</span>
                </Entry>
              </CommandPrimitive.Group>
            )}

            {results.orders.length > 0 && (
              <CommandPrimitive.Group heading="Oppdrag" className={GROUP}>
                {results.orders.map((o) => (
                  <Entry key={o.id} value={`order-${o.id}`} icon={ClipboardList} meta={`${o.ref} · ${STATUS[o.status] || o.status}`} onSelect={() => go(`/drift/oppdrag/${o.id}`)} testid="drift-cmd-result-order">
                    {o.title} <span className="text-muted-foreground">· {o.customer_name}</span>
                  </Entry>
                ))}
              </CommandPrimitive.Group>
            )}
            {results.inquiries.length > 0 && (
              <CommandPrimitive.Group heading="Henvendingar" className={GROUP}>
                {results.inquiries.map((i) => (
                  <Entry key={i.id} value={`inq-${i.id}`} icon={Inbox} meta={`${i.ref} · ${INQ_STATUS[i.status] || i.status}`} onSelect={() => go(`/drift/henvendingar/${i.id}`)} testid="drift-cmd-result-inquiry">
                    {i.company || i.name} <span className="text-muted-foreground">· {i.message}</span>
                  </Entry>
                ))}
              </CommandPrimitive.Group>
            )}
            {results.customers.length > 0 && (
              <CommandPrimitive.Group heading="Kundar" className={GROUP}>
                {results.customers.map((c) => (
                  <Entry key={c.id} value={`cust-${c.id}`} icon={c.type === "privat" ? User : Building2} meta={CUSTOMER_TYPE[c.type] || c.type} onSelect={() => go(`/drift/kundar/${c.id}`)} testid="drift-cmd-result-customer">
                    {c.name} {c.address && <span className="text-muted-foreground">· {c.address}</span>}
                  </Entry>
                ))}
              </CommandPrimitive.Group>
            )}
            {busy && term.length >= 2 && !hasRemote && <p className="px-3 py-2 text-[12.5px] text-muted-foreground">Søkjer …</p>}

            {pages.length > 0 && (
              <CommandPrimitive.Group heading="Gå til" className={GROUP}>
                {pages.map((n) => (
                  <Entry key={n.to} value={`page-${n.to}`} icon={n.icon} onSelect={() => go(n.to)} testid={`drift-cmd-page-${n.testid}`}>{n.label}</Entry>
                ))}
              </CommandPrimitive.Group>
            )}
            {actions.length > 0 && (
              <CommandPrimitive.Group heading="Handlingar" className={GROUP}>
                {actions.map((a) => (
                  <Entry key={a.label} value={`action-${a.label}`} icon={a.icon} onSelect={a.run} testid={a.testid}>{a.label}</Entry>
                ))}
              </CommandPrimitive.Group>
            )}
          </CommandPrimitive.List>
          <div className="flex items-center justify-between gap-4 border-t border-border/70 px-4 py-2 text-[11.5px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><span className="kbd">↑</span><span className="kbd">↓</span> for å velje</span>
            <span className="inline-flex items-center gap-1.5"><span className="kbd">↵</span> for å opne</span>
            <span className={cn("ml-auto inline-flex items-center gap-1", term.length < 2 && "opacity-60")}>Søk frå to teikn <ArrowRight className="h-3 w-3" strokeWidth={1.75} aria-hidden /></span>
          </div>
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}
